import {
  calendarQuerySchema,
  sortOrderSchema,
  type CalendarQuery,
  type InitiativeFilter,
  type SavedView,
  type SortOrder,
} from '@complan/contracts';
import {
  clearAllDimensions,
  parseFilterDimensions,
  writeFilterDimensions,
} from './filter-search-params';

/**
 * The URL search parameters are the single source of truth for the calendar's
 * filter state, year, orientation and sort (design.md, "State ownership";
 * R11.13). This module is the one place that reads them into typed values and
 * writes typed values back, so the query string, the TanStack Query key, the
 * Saved_View payload and the export request all derive from one representation
 * and cannot drift.
 *
 * Multi-value dimensions are repeated params (`?platformIds=a&platformIds=b`)
 * rather than comma-joined, matching the contracts filter grammar which accepts
 * repeated or comma-separated values. Tri-state Booleans serialise as the strings
 * `true` / `false`; an absent param means the dimension is unfiltered.
 */

/**
 * The filter grammar's dimension lists, key types and mutation helpers now live in
 * `filter-search-params.ts`, shared with every other screen that filters
 * Initiatives (`complan-governance` R6.1). They are re-exported here because this
 * module was their original home and the calendar's own components import them from
 * it; the shared module is the definition.
 */
export {
  BOOLEAN_KEYS,
  LIST_KEYS,
  clearDimension,
  removeListValue,
  setBooleanDimension,
  setListDimension,
  setSearchTerm,
  type BooleanFilterKey,
  type ListFilterKey,
} from './filter-search-params';

export interface CalendarViewState {
  readonly query: CalendarQuery;
  readonly sort: SortOrder;
}

/**
 * Parse the calendar view state from a URL. `year` comes from the route path
 * segment (`/calendar/:year`), everything else from the search string. Unparseable
 * values fall back to the schema defaults rather than throwing, so a hand-edited
 * or stale URL still opens (R11.15, R11.16).
 */
export function parseCalendarSearch(year: number, search: URLSearchParams): CalendarViewState {
  // Every filter dimension comes from the shared codec, so the calendar reads the
  // whole grammar - including the three governance dimensions of R6.11/R6.12, which
  // it renders no control for but must carry when a Filter_Context arrives from a
  // dashboard (R6.3).
  const raw: Record<string, unknown> = {
    ...parseFilterDimensions(search),
    planningYear: year,
  };

  const orientation = search.get('orientation');
  if (orientation) {
    raw['orientation'] = orientation;
  }

  const query = calendarQuerySchema.parse(raw);

  const sortField = search.get('sortField');
  const sortDirection = search.get('sortDirection');
  const sort = sortOrderSchema.parse(
    sortField || sortDirection
      ? {
          ...(sortField ? { field: sortField } : {}),
          ...(sortDirection ? { direction: sortDirection } : {}),
        }
      : undefined,
  );

  return { query, sort };
}

/**
 * Serialise view state back to a `URLSearchParams`, omitting the year (which lives
 * in the path) and every dimension left at its "unfiltered" value, so a default
 * view has an empty query string.
 */
export function toCalendarSearch(state: CalendarViewState): URLSearchParams {
  const params = new URLSearchParams();
  const { query, sort } = state;

  if (query.orientation !== 'month') {
    params.set('orientation', query.orientation);
  }

  writeFilterDimensions(params, query);

  if (sort.field !== 'initiative_ref') {
    params.set('sortField', sort.field);
  }
  if (sort.direction !== 'desc') {
    params.set('sortDirection', sort.direction);
  }

  return params;
}

/**
 * Reset every filter to its entry state (R11.1): the planning year stays (it is
 * the route path, not a query param), the orientation and sort are preserved
 * because they are view layout rather than a filter, and every filter dimension
 * is cleared. In terms of the query string, that is an empty string apart from a
 * non-default orientation and sort — so this simply drops every dimension param.
 */
export function resetFilters(current: URLSearchParams): URLSearchParams {
  // The three params this screen owns are layout, not filters, so they survive.
  return clearAllDimensions(current, ['orientation', 'sortField', 'sortDirection']);
}

/**
 * The view state a Saved_View reproduces, split into the path year and the search
 * string the calendar route reads (R11.11). A Saved_View stores the same filter
 * grammar the screen uses, so applying it is a pure re-serialisation through the
 * one codec — there is no private saved-view encoding to translate. The returned
 * `search` omits the year exactly as {@link toCalendarSearch} does; the caller
 * navigates to `/calendar/${year}?${search}`.
 *
 * The view's *effective* filters are used verbatim: the server has already dropped
 * references that no longer exist (R11.16) and retained deactivated ones (R11.15),
 * so what arrives here is precisely what the calendar should now evaluate. The
 * `deactivatedFilterValues` / `omittedFilterValueCount` the view also carries are
 * surfaced by the menu as an indication, not re-applied to the URL.
 */
export function savedViewToView(view: SavedView): { year: number; search: URLSearchParams } {
  const query = calendarQuerySchema.parse({
    ...(view.filters as InitiativeFilter),
    orientation: view.orientation,
  });
  const search = toCalendarSearch({ query, sort: view.sort });
  return { year: query.planningYear, search };
}
